import React from 'react';
import { TabsRoot } from './components/root';
import { TabPanel } from './components/panel';
import { TabItemId, TabsProps } from './model';
import { TabHeader } from './components/header';
import { TabHeaderItem } from './components/headerItem';
import { TabIndicator } from './components/indicator';

export * from './model';

export const Tabs = <C, I extends TabItemId>({
  items,
  beforeHeaderContent: headerBeforeContent,
  sx,
  itemSx,
  itemId,
  keepAllItemsMounted,
  renderItemLabel,
  renderBody = ({ children }) => children,
  renderItemBody,
  onItemChange,
}: TabsProps<C, I>): ReturnType<React.FC> => {
  const [activeItemId, setActiveItemId] = React.useState<TabItemId | undefined>(itemId ? itemId : undefined);

  const itemsForBody = React.useMemo(() => {
    if (keepAllItemsMounted) {
      return items;
    }

    if (activeItemId === undefined) {
      return [];
    }

    return items.filter((item) => item.id === activeItemId);
  }, [activeItemId, items, keepAllItemsMounted]);

  const handleHeaderItemClick = (selectedIndex: TabItemId) => {
    if (itemId) {
      setActiveItemId(selectedIndex);
    } else {
      setActiveItemId(activeItemId === selectedIndex ? undefined : selectedIndex);
    }
    onItemChange?.(selectedIndex as I);
  };

  return (
    <TabsRoot>
      <TabHeader sx={sx}>
        {headerBeforeContent}
        {items.map((item) => {
          const { label, id } = item;
          const isActive = activeItemId === id;

          return (
            <TabHeaderItem data-active={isActive} key={id} onClick={() => handleHeaderItemClick(id)} role="button" sx={itemSx} tabIndex={0}>
              {renderItemLabel ? renderItemLabel({ isActive, value: label, item }) : label}
              <TabIndicator hidden={!isActive} />
            </TabHeaderItem>
          );
        })}
      </TabHeader>

      {renderBody({
        activeItemId: activeItemId as I,
        children: itemsForBody.map(({ body, id }) => {
          const isActive = activeItemId === id;
          return (
            <TabPanel key={id} ownerState={{ isActive }}>
              {renderItemBody ? renderItemBody({ isActive, value: body }) : (body as unknown as React.ReactNode)}
            </TabPanel>
          );
        }),
      })}
    </TabsRoot>
  );
};
